import type {
  JSONObject,
  JSONValue,
} from '@risksmart-app/domain/src/types/common.types';
import { isPlainObject } from 'lodash';

/** Form-data keys that carry `File[]` maps, not answer values. */
const FILE_KEYS = new Set(['files', 'newFiles', 'updatedFiles']);

/**
 * Recursively narrows a runtime form value to its JSON-safe shape. JSONForms
 * writes whatever the field control produces straight into the form data —
 * primitives, but also `string[]` for multiselects and nested objects for
 * composite controls — while the `ResponseData` index signature understates
 * that as primitives-plus-file-maps. Non-JSON leaves (`File` instances,
 * functions, dates) are dropped from objects, matching what the legacy Apollo
 * path's `jsonb` serialisation would have discarded; inside arrays they become
 * `null` so answer positions are preserved (mirroring `JSON.stringify`, which
 * nulls unserialisable array elements rather than shifting later ones).
 * Non-finite numbers (`NaN`, `±Infinity`) also map to `null` — they are not
 * JSON-safe and would otherwise be rejected by the `jsonb` write.
 */
const toJsonValue = (value: unknown): JSONValue | undefined => {
  if (value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (Array.isArray(value)) {
    const items: JSONValue[] = [];
    for (const item of value) {
      const converted = toJsonValue(item);
      // Preserve positions: an unsupported element becomes null (as
      // JSON.stringify does) rather than being dropped, which would shift
      // subsequent answers.
      items.push(converted === undefined ? null : converted);
    }

    return items;
  }

  if (typeof value === 'object' && isPlainObject(value)) {
    const record: JSONObject = {};
    for (const [key, entry] of Object.entries(value)) {
      const converted = toJsonValue(entry);
      if (converted !== undefined) {
        record[key] = converted;
      }
    }

    return record;
  }

  return undefined;
};

/**
 * Builds the JSONB save payload from the CustomForm data. Mirrors the legacy
 * Apollo write, which spread the ENTIRE form data (multiselect arrays and
 * nested objects included) and nulled `files`/`newFiles`/`updatedFiles`
 * before sending it as a `jsonb` variable — attachments travel via the
 * separate file-update flow. Those file-valued keys are dropped here (rather
 * than nulled) so the result is a plain `JSONObject` that the tRPC
 * `responseData` input and the legacy `jsonb` variable both accept; every
 * other entry is carried through with its full JSON shape.
 *
 * The parameter is `Record<string, unknown>` rather than `ResponseData`
 * because the `ResponseData` index signature understates what JSONForms puts
 * in the form data at runtime (multiselect `string[]`, nested objects);
 * `ResponseData` remains assignable, so call sites are unchanged.
 */
export const toResponseDataJsonb = (
  data: Record<string, unknown>
): JSONObject => {
  const payload: JSONObject = {};

  for (const [key, value] of Object.entries(data)) {
    if (FILE_KEYS.has(key)) {
      continue;
    }

    const converted = toJsonValue(value);
    if (converted !== undefined) {
      payload[key] = converted;
    }
  }

  return payload;
};
